import { createContext } from "react";
import type { Permission, User } from "@/domain/user";
import type { TenantKey } from "@/domain/space";
import type { DeviceDoc } from "@/domain/session";
import type { DeviceInfo } from "@/domain/device_info";

export interface LoginOptions {
  /** "공용 PC입니다" 체크(PC 만 의미 있음) — 창을 닫으면 로그아웃, 기기로 등록하지 않음 */
  sharedPc?: boolean;
}

/** 로그인 결과 — 기기 3대가 꽉 차면 Login 화면이 E-3/E-4 단계를 띄운다(12 설계 §7-3) */
export type LoginOutcome =
  | { status: "ok"; user: User }
  | { status: "device_limit"; user: User; devices: DeviceDoc[]; thisDevice: DeviceInfo };

/** 앱이 스스로 로그아웃할 때 남기는 접속 기록 종류 */
export type LogoutKind = "logout" | "idle_logout";

export interface AuthContextValue {
  user: User | null;
  loading: boolean;
  error: Error | null;
  login: (loginId: string, password: string, opts?: LoginOptions) => Promise<LoginOutcome>;
  /** 3대 가득 단계에서 고른 기기를 해제하고 이 기기를 등록한 뒤 로그인을 마친다 */
  resolveDeviceLimit: (releaseDeviceId: string) => Promise<void>;
  /** 3대 가득 단계에서 [취소] — 로그아웃 */
  cancelLogin: () => Promise<void>;
  /** 로그아웃(접속 기록 포함). kind 기본 "logout" */
  logout: (kind?: LogoutKind) => Promise<void>;
  /** 이 기기 ID — 공용 PC 로그인(기기 미등록)이면 null */
  deviceId: string | null;
  /** 이 기기 문서(구독). 등록 전·공용 PC·rules 미배포면 null */
  device: DeviceDoc | null;
  /** 이 탭이 공용 PC 로그인인지 */
  sharedSession: boolean;
  /**
   * 권한 판정. `tenant` = 판정 기준 법인(2026-10-20 1단계 자리).
   * 지금은 법인별 역할 데이터가 없어 tenant 를 받아도 계정 전체 권한으로 판정한다(결과 동일).
   * 2단계: user.tenantRoles[tenant] 로 바꿀 자리. 법인 소속(가상 MOCK_MEMBERSHIP)까지 따지려면
   * 공간 안에서 `useSpace().hasPermIn(key, tenant?)` 를 쓴다.
   */
  hasPerm: (key: Permission, tenant?: TenantKey) => boolean;
}

export const AuthContext = createContext<AuthContextValue | null>(null);
